import { dictionaries } from "../lib/i18n";
import { daylightSummary, formatDate, formatDuration, formatHours } from "../lib/format";
import type { Amenity, Itinerary, UiLang, Stop } from "../lib/types";
import { EmergencyCard } from "./EmergencyCard";

interface Props {
  itinerary: Itinerary;
  lang: UiLang;
}

/*
 * Hvor mye av hver liste som blir med på arket.
 *
 * Papir har en fysisk grense skjermen ikke har, og en reiseplan som blir tolv
 * sider blir ikke tatt med. Tallene er valgt så et stopp holder seg innenfor en
 * halv spalte: nok til å velge mellom, ikke nok til å bli et oppslagsverk.
 */
const OFFERS_ON_PAPER = 4;
const LODGING_ON_PAPER = 2;
const PROTECTED_ON_PAPER = 2;

/**
 * Fredete bygninger per stopp på papir.
 *
 * To, ikke fire som skjermen viser. Bergen har flere hundre fredete
 * bygninger, og en liste er ikke et reisemål — de to nærmeste er.
 */
const HERITAGE_ON_PAPER = 2;
const FUELLING_ON_PAPER = 2;
const FERRY_TIMES_ON_PAPER = 6;

/**
 * Reiseplanen som et dokument å ta med på papir.
 *
 * Dette finnes fordi utskrift av skjermen var halvt ubrukelig, og grunnen er
 * strukturell: appen viser én dag om gangen bak faner. Det er riktig på skjerm —
 * man planlegger en dag av gangen — men en utskrift av dag 3 er ikke en
 * reiseplan. Man trykket «skriv ut» og fikk en tredel av turen.
 *
 * Løsningen er ikke å tegne om skjermen for papirets skyld, men å ha et eget
 * dokument for papir. Da kan begge være riktige: fanene på skjerm, alle dagene
 * på papir, formatert for et A4-ark framfor for et nettleservindu.
 *
 * Rekkefølgen er valgt for papir. Nødnumrene står på første side, ikke bakerst,
 * fordi det er den siden man finner igjen når man trenger dem — og fordi hele
 * poenget med å ha planen på papir er at den virker når telefonen er død eller
 * uten dekning. Det er nettopp da nødnumrene er vanskeligst å finne.
 */
export function Roadbook({ itinerary, lang }: Props) {
  const t = dictionaries[lang];

  const first = itinerary.days.at(0)?.stops.at(0);
  const last = itinerary.days.at(-1)?.stops.at(-1);

  /* Alle stoppene på ruta, til posisjonslista i nødkortet. */
  const allStops = itinerary.days.flatMap((day) => day.stops);

  return (
    <div className="roadbook print-only">
      <header className="roadbook-head">
        <h1>
          {first?.name} <span>→</span> {last?.name}
        </h1>
        <p className="roadbook-meta">
          {itinerary.days.length} {t.roadbookDays} · {itinerary.totalStops} {t.roadbookStops} ·{" "}
          {itinerary.totalDistanceKm} {t.unitKm} ·{" "}
          {formatHours(itinerary.totalTravelHours, lang)} {t.travelSuffix} ·{" "}
          {itinerary.highestPointM} {t.unitMasl}
          {itinerary.request.startDate && ` · ${formatDate(itinerary.request.startDate, lang)}`}
        </p>
      </header>

      {/*
        Advarslene for hele reisen.

        På skjerm er de `.banner`, som utskriften skjuler sammen med alt annet
        interaktivt. Uten dette leddet forsvant de derfor helt fra papiret — og
        en advarsel om at en fjellovergang er vinterstengt er blant det man mest
        trenger på et ark i bilen.
      */}
      {itinerary.warnings.length > 0 && (
        <ul className="roadbook-warnings">
          {itinerary.warnings.map((warning) => (
            <li key={warning}>⚠ {warning}</li>
          ))}
        </ul>
      )}

      {/*
        Nødnumrene først. Se komponentens egen begrunnelse — papirutskriften er
        det man har når telefonen ikke virker, og det er da dette trengs.
      */}
      <section className="roadbook-emergency">
        <EmergencyCard stops={allStops} lang={lang} />
      </section>

      {/*
        Ferjene ruta må over, med avgangstidene.

        Dette hører på papir mer enn på skjerm. En ferje går hver time eller
        hver annen, ikke hvert femte minutt, og en plan som ikke sier når den
        går er en plan man ikke kan følge — man står på kaia og gjetter. Tidene
        er de planlagte; avvik i dag hører på skjerm.
      */}
      {itinerary.ferries && itinerary.ferries.length > 0 && (
        <section className="roadbook-ferries">
          <h2>{t.ferryTitle}</h2>
          {itinerary.ferries.map((crossing) => (
            <p key={`${crossing.stopId}-${crossing.toName}`}>
              <b>
                {crossing.quayName} → {crossing.toName}
              </b>
              {crossing.departures.length > 0 && (
                <span>
                  {" · "}
                  {crossing.departures
                    .slice(0, FERRY_TIMES_ON_PAPER)
                    .map((d) => clock(d.departure, lang))
                    .join(", ")}
                </span>
              )}
            </p>
          ))}
        </section>
      )}

      {/*
        Kostnadsrammen.

        Tallene er et anslag og sier det selv, men et anslag på papir er verdt
        mer enn et presist tall man ikke har med seg — det er dette man regner
        mot underveis når man vurderer en ekstra natt.
      */}
      {/*
        Klesrådet.

        Det hører på papir framfor noe annet her. En klimanormal er ikke
        ferskvare — den bygger på tretti år og gjelder til normalperioden
        endres, altså i et tiår — og en pakkeliste er nettopp det man vil ha
        skrevet ut mens kofferten står åpen.
      */}
      {itinerary.packing && (
        <section className="roadbook-packing">
          <h2>{t.packingTitle}</h2>
          <p>{itinerary.packing.headline}</p>
          {/*
            Klær og annet, som på skjermen — se PackingPanel.tsx. Søkeknappene
            der er borte her med vilje: en lenke er verdiløs på et ark, og
            navnet på varen er hele svaret man trenger mens kofferten er åpen.
          */}
          <p className="roadbook-packing-heading">{t.packingClothing}</p>
          <ul className="roadbook-packing-list">
            {itinerary.packing.items
              .filter((item) => item.category === "clothing")
              .map((item) => (
                <li key={item.key}>{item.label}</li>
              ))}
          </ul>
          <p className="roadbook-packing-heading">{t.packingGear}</p>
          <ul className="roadbook-packing-list">
            {itinerary.packing.items
              .filter((item) => item.category === "gear")
              .map((item) => (
                <li key={item.key}>{item.label}</li>
              ))}
          </ul>
          <small>
            {t.packingWetNote(Math.round(itinerary.packing.wetShare * 100))}
            {itinerary.packing.snow && ` ${t.packingSnowNote}`}
          </small>
        </section>
      )}

      {itinerary.budget && (
        <section className="roadbook-budget">
          <h2>{t.budgetTitle}</h2>
          <ul>
            {itinerary.budget.lines.map((line) => (
              <li key={line.key}>
                {line.label}
                <span>
                  {line.low.toLocaleString("nb-NO")}–{line.high.toLocaleString("nb-NO")} kr
                </span>
              </li>
            ))}
            <li className="roadbook-budget-total">
              <b>{t.budgetTotal}</b>
              <span>
                <b>
                  {itinerary.budget.low.toLocaleString("nb-NO")}–
                  {itinerary.budget.high.toLocaleString("nb-NO")} kr
                </b>
              </span>
            </li>
          </ul>
          <small>{itinerary.budget.disclaimer}</small>
        </section>
      )}

      {itinerary.days.map((day) => (
        /*
         * Hver dag begynner på nytt ark. `break-inside: avoid` på stoppene
         * hindrer at et sted deles over to sider midt i beskrivelsen — å bla om
         * for å lese resten av navnet på et fossefall er ikke et dokument.
         */
        <section className="roadbook-day" key={day.dayNumber}>
          <h2>
            {t.dayTab(day.dayNumber)}
            {day.date && <small>{formatDate(day.date, lang)}</small>}
          </h2>

          <ul className="roadbook-facts">
            <li>
              {day.distanceKm} {t.unitKm}
            </li>
            <li>
              {formatHours(day.travelHours, lang)} {t.travelSuffix}
            </li>
            {day.daylight && <li>{daylightSummary(day.daylight, lang)}</li>}
            {day.golden?.evening && (
              <li>
                {t.goldenBest} {clock(day.golden.evening.from, lang)}
              </li>
            )}
          </ul>

          {day.notes.length > 0 && (
            <ul className="roadbook-notes">
              {day.notes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          )}

          {/*
            Avgangene, for den som reiser kollektivt.

            Dette er det mest utskriftsverdige appen har og manglet helt: en
            reiseplan på papir uten togtidene er en liste over steder man ikke
            vet hvordan man kommer til. Tidene er de planlagte fra Entur —
            sanntid hører på skjerm, for den er utdatert i det arket forlater
            skriveren.
          */}
          {day.legs.length > 0 && (
            <table className="roadbook-legs">
              <tbody>
                {day.legs.map((leg, i) => (
                  <tr key={`${leg.line}-${leg.aimedDeparture}-${i}`}>
                    <td className="roadbook-leg-line">{leg.line}</td>
                    <td>
                      {leg.from} → {leg.to}
                    </td>
                    <td className="roadbook-leg-time">
                      {clock(leg.aimedDeparture, lang)}–{clock(leg.aimedArrival, lang)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/*
            Etappene mellom stoppene, som én linje hver.

            Avgangstabellen over gjelder bare den kollektivreisende, og bare
            strekningen inn i dagen. Dette er hele dagens forflytning —
            avstand, anslått tid og hva slags etappe det er — og det er den
            opplysningen et papirark faktisk kan bære uten dekning.
          */}
          {day.connections.length > 0 && (
            <ul className="roadbook-connections">
              {day.connections.map((connection) => (
                <li key={`${connection.fromStopId}-${connection.toStopId}`}>
                  <span className="roadbook-connection-route">
                    {connection.fromName} → {connection.toName}
                  </span>
                  <span className="roadbook-connection-facts">
                    {t.connectionSummary(
                      connection.straightKm,
                      formatDuration(Math.round(connection.hours * 60), lang),
                      t.modeName[connection.mode].toLowerCase(),
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {/*
            Stoppene i to spalter. En reiseplan er korte avsnitt, og korte
            avsnitt over full A4-bredde gir linjer på 120 tegn som øyet mister
            underveis. To spalter gir omtrent 60, som er der lesbarheten ligger.
          */}
          <div className="roadbook-stops">
            {day.stops.map((stop) => (
              <article className="roadbook-stop" key={stop.id}>
                <h3>
                  {stop.name}
                  <small>
                    {stop.elevationM} {t.unitMasl}
                  </small>
                </h3>
                <p className="roadbook-coords">
                  {stop.lat.toFixed(4)}, {stop.lon.toFixed(4)}
                </p>
                <p>{stop.blurb}</p>

                {/*
                  Hva stedet har å tilby — den håndskrevne lista fra offers.ts.

                  Den manglet på papir, og det var det største hullet: arket
                  fortalte hvor man skulle, men ikke hva man skulle gjøre der.
                  Navnene står uoversatt med vilje, se offers.ts — det er dette
                  man spør etter på stedet.
                */}
                {stop.offers && stop.offers.length > 0 && (
                  <ul className="roadbook-offers">
                    {stop.offers.slice(0, OFFERS_ON_PAPER).map((offer) => (
                      <li key={offer.name}>
                        <b>{offer.name}</b>
                        <span> · {t.offerKind[offer.kind]}</span>
                        {offer.note && <small>{offer.note}</small>}
                      </li>
                    ))}
                  </ul>
                )}

                {stop.hike && (
                  <div className="roadbook-hike">
                    <p>
                      <b>{t.hikeTitle}:</b> {stop.hike.hoursMin}–{stop.hike.hoursMax} t,{" "}
                      {stop.hike.distanceKm} km, {stop.hike.ascentM} {t.unitMasl}
                      {stop.hike.timeWarning && ` — ${stop.hike.timeWarning}`}
                    </p>

                    {/*
                      Hvor bilen slutter og stien begynner, og sesongen.

                      På papir er dette selve bruksverdien: man står på en
                      parkeringsplass og skal vite om det er den riktige.
                    */}
                    <p>
                      <b>{t.hikeTrailhead}:</b> {stop.hike.trailhead}
                    </p>
                    <p>
                      <b>{t.hikeSeason}:</b> {stop.hike.season}
                    </p>

                    {/*
                      Firmaene og bestillingsfristen.

                      Dette hører på papir mer enn noe annet i planen: fristen er
                      dager i forveien, så den leses før man reiser, og «bestill
                      3 dager før» er ubrukelig hvis den bare finnes bak et klikk
                      på et sted man alt har forlatt.
                    */}
                    {stop.hike.guides.length > 0 && (
                      <p>
                        <b>{t.hikeGuidesTitle}:</b>{" "}
                        {stop.hike.guides
                          .map(
                            (guide) =>
                              `${guide.operator} (${t.hikeBookAhead(guide.bookAheadDays)})`,
                          )
                          .join(" · ")}
                      </p>
                    )}
                  </div>
                )}

                {stop.winterWarning && (
                  <p className="roadbook-warn">⚠ {stop.winterWarning}</p>
                )}

                {/*
                  Vinteren, på papir.

                  Roadbooken er den utgaven man har med seg der dekningen er
                  borte, og det er nettopp på en vinterdag på fjellet den er
                  borte. Nettadressene er verdiløse på papir og utelates
                  derfor; navnet, sesongen og bookingtiden er ikke — «Femundløpet,
                  februar» er nok til å spørre i resepsjonen, og
                  «bestill 90 dager i forveien» er nok til å skjønne at det ikke
                  ordner seg på stedet.
                */}
                {stop.winter && stop.winter.activities.length > 0 && (
                  <div className="roadbook-winter">
                    <p className="roadbook-label">{t.winterTitle}</p>
                    <ul>
                      {stop.winter.activities.map((activity) => (
                        <li key={activity.name}>
                          <b>{activity.name}</b> — {t.winterKind[activity.kind]},{" "}
                          {t.winterMonths(
                            activity.months.map((m) => t.monthShort[m - 1]).join(", "),
                          )}
                          {activity.bookAheadDays !== undefined && (
                            <> · {t.winterBookAhead(activity.bookAheadDays)}</>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {stop.winter?.access && !stop.winter.access.openThisMonth && (
                  <p className="roadbook-warn">
                    ⚠ {t.winterStatus[stop.winter.access.status]}: {stop.winter.access.note}
                  </p>
                )}

                {/* Regnplanen. Den står på skjerm, og trengs like mye på papir. */}
                {stop.indoorAlternative && (
                  <p className="roadbook-indoor">
                    <b>{t.whenItRains}</b> {stop.indoorAlternative}
                  </p>
                )}

                {/*
                  Vernet natur.

                  Lenkene til forskriften er verdiløse på papir, men navnet og
                  vernetypen er det ikke: «nasjonalpark» og «naturreservat» har
                  ulike regler for bål, telt og ferdsel, og på papir er dette
                  varselet om at det finnes regler i det hele tatt.
                */}
                {/*
                  Fredete bygninger.

                  Med av samme grunn som verneområdene, og med samme
                  begrensning: lenkene er verdiløse på papir, navnet og
                  dateringen er det ikke. «Urnes stavkyrkje, om lag 1130» er
                  grunnen man kjørte dit, og den skal stå der.
                */}
                {stop.heritage.length > 0 && (
                  <p className="roadbook-heritage">
                    <b>{t.heritageTitle}:</b>{" "}
                    {stop.heritage
                      .slice(0, HERITAGE_ON_PAPER)
                      .map((site) => (site.dated ? `${site.name} (${site.dated})` : site.name))
                      .join(" · ")}
                  </p>
                )}

                {stop.protectedAreas && stop.protectedAreas.length > 0 && (
                  <p className="roadbook-protected">
                    <b>{t.protectedTitle}:</b>{" "}
                    {stop.protectedAreas
                      .slice(0, PROTECTED_ON_PAPER)
                      .map((area) => `${area.name} (${area.kind})`)
                      .join(" · ")}
                  </p>
                )}

                {/*
                  Lading og bensin, ikke spisesteder.

                  Skjermen viser alle tre. På papir er de to første noe man
                  planlegger etter — rekkevidden avgjør om dagen går — mens
                  spisestedet er noe man finner når man er der.
                */}
                {fuelling(stop).length > 0 && (
                  <p className="roadbook-fuel">
                    {fuelling(stop)
                      .map(
                        (a) =>
                          `${a.kind === "charging" ? t.charging : t.fuel}: ${a.name} (${a.distanceKm} km)`,
                      )
                      .join(" · ")}
                  </p>
                )}

                {/*
                  To overnattinger per stopp, den nærmeste av hvert slag som
                  finnes. Hele lista hører på skjerm der man velger; ett enkelt
                  navn er for lite når det viser seg å være fullt.
                */}
                {stop.lodging && stop.lodging.length > 0 && (
                  <p className="roadbook-sleep">
                    <b>{t.lodgingTitle}:</b>{" "}
                    {stop.lodging
                      .slice(0, LODGING_ON_PAPER)
                      .map(
                        (bed) =>
                          `${bed.name} — ${t.lodgingKind[bed.kind]}${bed.dnt ? " (DNT)" : ""}, ${bed.distanceKm} km`,
                      )
                      .join(" · ")}
                  </p>
                )}
              </article>
            ))}
          </div>

          {/*
            Plass til å skrive selv.

            Meldt fra bruk: «kan vi lage en liten seksjon på siden med
            additional notes, så turister kan bare skrive den ut og skrive
            notater». Det er nettopp det papir er godt til, og det er den ene
            tingen skjermen ikke kan: en avgang som ble flyttet, navnet på
            hytta man fikk anbefalt, hvor bilen står.

            Per dag og ikke bakerst, fordi et notat hører til dagen det gjelder
            — en samleside til slutt betyr at man blar mens man skriver.

            Linjer, ikke et tomt felt: uten dem skriver folk mindre, og
            skriften sklir nedover på et ark uten holdepunkt.
          */}
          <div className="roadbook-jot">
            <span className="roadbook-jot-label">{t.roadbookNotes}</span>
            <span className="roadbook-line" />
            <span className="roadbook-line" />
            <span className="roadbook-line" />
          </div>
        </section>
      ))}

      <footer className="roadbook-foot">
        <p>{t.roadbookFooter}</p>
      </footer>
    </div>
  );
}

/**
 * Nærmeste lading og bensin, ett av hvert slag.
 *
 * Sortert på avstand innen hver type framfor å ta de to første i lista: står to
 * ladere nærmest, får arket to ladere og ingen bensinstasjon, og det er den ene
 * opplysningen en bensinbil trenger.
 */
function fuelling(stop: Stop): Amenity[] {
  const nearest = (kind: Amenity["kind"]) =>
    (stop.amenities ?? [])
      .filter((a) => a.kind === kind)
      .sort((a, b) => a.distanceKm - b.distanceKm)
      .slice(0, 1);

  return [...nearest("charging"), ...nearest("fuel")].slice(0, FUELLING_ON_PAPER);
}

/** Klokkeslettet i brukerens tidssone. Tidene kommer som ISO i UTC. */
function clock(iso: string, lang: UiLang): string {
  const stamp = new Date(iso);
  if (Number.isNaN(stamp.getTime())) return "";
  return stamp.toLocaleTimeString(lang === "no" ? "nb-NO" : "en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
}
